import { useState, useEffect } from "react";
import { User } from "lucide-react";
import { resolveImageUrl } from "../../utils/imageUrl";

/**
 * Robust avatar that gracefully falls back to a lucide User icon
 * when the image is missing OR fails to load (404 / wiped disk).
 *
 * Props:
 *   src:        backend-relative URL (e.g. "/api/uploads/profiles/abc.png")
 *               OR full absolute URL. null/undefined → fallback.
 *   name:       used for the alt text and (future) initials
 *   size:      "xs" | "sm" | "md" | "lg" | "xl" — sets pixel dimensions
 *   className: extra classes for the wrapper
 */
const SIZES = {
  xs: "w-5 h-5 text-[10px]",
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
  xl: "w-16 h-16 text-lg",
};

const ICON_SIZES = {
  xs: "w-3 h-3",
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
  xl: "w-8 h-8",
};

export const Avatar = ({ src, name, size = "md", className = "" }) => {
  const [errored, setErrored] = useState(false);

  // Reset error state if src changes (e.g. user uploads new pic)
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const dim = SIZES[size] || SIZES.md;
  const iconDim = ICON_SIZES[size] || ICON_SIZES.md;

  // Build full URL — accept absolute, root-relative, legacy or stale-domain
  const url = resolveImageUrl(src);

  if (!url || errored) {
    const initial = (name || "").trim().charAt(0).toUpperCase();
    return (
      <div
        className={`${dim} rounded-full bg-[#1F1F1F] border border-[#2A2A2A] flex items-center justify-center text-zinc-400 font-bold ${className}`}
        aria-label={name || "User"}
      >
        {initial || <User className={iconDim} />}
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={name || "User"}
      onError={() => setErrored(true)}
      className={`${dim} rounded-full object-cover ${className}`}
      loading="lazy"
    />
  );
};

export default Avatar;
